"use client";

import React from "react";
import { motion } from "framer-motion";
import { Heart, Loader2 } from "lucide-react";
import { cn } from "@/src/lib/utils";
import HeartOutlineIcon from "@/src/shared/components/icons/HeartOutlineIcon";
import { useWishlistHeart } from "../hooks/useWishlistHeart";

interface IWishlistHeartButtonProps {
  listingId: number;
  /**
   * `overlay` sits on a photo (white outline); `plain` sits on white (dark outline);
   * `labeled` is Airbnb's details-page button: a 16px heart and an underlined "Save"
   * ("Saved", the heart filled, once saved).
   */
  variant?: "overlay" | "plain" | "labeled";
  size?: number;
  className?: string;
}

/**
 * The save heart, as in the app: filled brand pink when saved, an outline when not, a
 * spinner while the server answers, and a pop when it turns saved. It sits inside
 * listing cards that are links, so its click never reaches the card.
 */
const WishlistHeartButton: React.FC<IWishlistHeartButtonProps> = ({
  listingId,
  variant = "overlay",
  size = 24,
  className,
}) => {
  const { isSaved, isPending, toggle } = useWishlistHeart(listingId);

  if (variant === "labeled") {
    return (
      <button
        type="button"
        aria-label={isSaved ? "Remove from wishlist" : "Save to wishlist"}
        aria-pressed={isSaved}
        disabled={isPending}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void toggle();
        }}
        className={cn(
          "inline-flex h-[34px] shrink-0 items-center gap-2 rounded-lg p-2 text-14 font-medium leading-[18px] text-ink underline transition-colors hover:bg-[#F7F7F7] disabled:cursor-wait",
          className,
        )}
      >
        {isPending ? (
          <Loader2 size={16} strokeWidth={2.5} className="animate-spin" />
        ) : (
          <motion.span
            key={isSaved ? "saved" : "unsaved"}
            initial={isSaved ? { scale: 0.55 } : false}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 520, damping: 11 }}
            className={cn("flex", isSaved && "text-brand-600")}
          >
            <HeartOutlineIcon filled={isSaved} />
          </motion.span>
        )}
        {isSaved ? "Saved" : "Save"}
      </button>
    );
  }

  return (
    <button
      type="button"
      aria-label={isSaved ? "Remove from wishlist" : "Save to wishlist"}
      aria-pressed={isSaved}
      disabled={isPending}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        void toggle();
      }}
      className={cn(
        "flex shrink-0 items-center justify-center p-2 transition-transform active:scale-90 disabled:cursor-wait",
        className,
      )}
    >
      {isPending ? (
        <Loader2
          size={size - 6}
          strokeWidth={2.5}
          className={cn(
            "animate-spin",
            variant === "overlay" ? "text-white" : "text-ink-black",
          )}
        />
      ) : (
        <motion.span
          // Re-mounts on save so the pop plays each time it turns saved.
          key={isSaved ? "saved" : "unsaved"}
          initial={isSaved ? { scale: 0.55 } : false}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 520, damping: 11 }}
          className="flex"
        >
          <Heart
            size={size}
            strokeWidth={2}
            className={cn(
              isSaved
                ? "fill-brand-600 text-brand-600"
                : variant === "overlay"
                  ? "fill-black/40 text-white drop-shadow"
                  : "fill-transparent text-ink-black",
            )}
          />
        </motion.span>
      )}
    </button>
  );
};

export default WishlistHeartButton;
